import * as Yup from "yup";

// Prepend https:// only when the value carries no scheme at all (no `://`).
// Lenient on purpose: this feeds the Formik `website` field and the submit path,
// which must tolerate a legacy persisted website of any scheme. The onboarding
// form has no editable website field, so rewriting or rejecting the stored value
// would permanently block "Continue" for such an organisation.
// Matches a URI scheme only at the START of the string (`scheme://`), so an
// embedded `://` — e.g. inside a query parameter — is not mistaken for a leading
// scheme.
const HAS_LEADING_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

export const normalizeWebsiteUrl = (input: string): string => {
  const trimmed = input.trim();
  if (trimmed === "") {
    return "";
  }
  return HAS_LEADING_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Strict variant for the AI-prefill input, where the user types a URL to fetch
// company data. Only http(s) counts as already-qualified (matching `prepareUrl`
// in commons/form-fields/helpers), so a non-web scheme (`ftp://`, `file://`, …)
// gets `https://` prepended and then fails validation instead of being sent to
// the prefill query or written into the form.
const normalizeWebsiteUrlStrict = (input: string): string => {
  const trimmed = input.trim();
  if (trimmed === "") {
    return "";
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Lenient schema for the Formik `website` field and the submit path.
export const websiteUrlValidationSchema = (): Yup.StringSchema<
  string | null | undefined
> =>
  Yup.string()
    .transform(value =>
      typeof value === "string" && value ? normalizeWebsiteUrl(value) : value
    )
    .url("Please enter a valid website URL")
    .nullable();

// Strict schema for the AI-prefill input — rejects non-http(s) schemes.
export const websiteUrlPrefillValidationSchema = (): Yup.StringSchema<
  string | null | undefined
> =>
  Yup.string()
    .transform(value =>
      typeof value === "string" && value
        ? normalizeWebsiteUrlStrict(value)
        : value
    )
    .url("Please enter a valid website URL")
    .nullable();
